import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import PublicNav from './PublicNav'
import Footer from './Footer'

/**
 * The canonical origin. Apex, never www.
 *
 * www.praenum.com currently answers 200 rather than redirecting (measured
 * 2026-08-13), so every page exists at two addresses with nothing saying which
 * is real, and whatever ranking signal the site has is split between them. The
 * canonical link below is the application-side half of that fix. The redirect
 * itself is an nginx change tracked as issue #30 and is NOT done here.
 */
const SITE_ORIGIN = 'https://praenum.com'

/**
 * Sets the document <title> for the current page and restores the default on
 * unmount. SPA-friendly per-page titles without a meta framework.
 */
export function useDocumentTitle(title) {
  useEffect(() => {
    const previous = document.title
    document.title = title ? `${title} · Praenum` : 'Praenum'
    return () => {
      document.title = previous
    }
  }, [title])
}

/** Create-or-update a <meta> in <head>, keyed on name= or property=. */
function setMeta(keyAttr, keyValue, content) {
  if (!content) return
  let el = document.head.querySelector(`meta[${keyAttr}="${keyValue}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(keyAttr, keyValue)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

/**
 * Per-page SEO metadata: title, description, canonical URL and the Open Graph
 * pair that renders whenever someone shares a link.
 *
 * ## Why this exists
 *
 * Until 2026-08-13 every route served the SAME title and description. Titles
 * were at least patched in afterwards by `useDocumentTitle`; the meta
 * description was set once in `index.html` and **never changed at all**. So the
 * markup a crawler used to build a result for `/pricing`, `/about` and `/faq`
 * was identical to the homepage's, and four pages competed as near-duplicates
 * of one.
 *
 * ⚠️ **This is a client-side fix and that is a real limitation.** The HTML
 * leaving nginx still carries `index.html`'s defaults; these values only appear
 * after the bundle executes. Google renders JavaScript and will see them, but
 * crawlers that do not (and most social-card scrapers) will keep reading the
 * static tags. That is why `index.html` keeps a sensible sitewide default
 * rather than a homepage-specific one. Serving real per-route HTML needs
 * prerendering or SSR, which is a bigger change than this one.
 */
export function useSeo({ title, description }) {
  const { pathname } = useLocation()
  useDocumentTitle(title)

  useEffect(() => {
    const fullTitle = title ? `${title} · Praenum` : 'Praenum'
    const url = `${SITE_ORIGIN}${pathname === '/' ? '' : pathname}`

    setMeta('name', 'description', description)
    setMeta('property', 'og:title', fullTitle)
    setMeta('property', 'og:description', description)
    setMeta('property', 'og:url', url)

    let link = document.head.querySelector('link[rel="canonical"]')
    if (!link) {
      link = document.createElement('link')
      link.setAttribute('rel', 'canonical')
      document.head.appendChild(link)
    }
    link.setAttribute('href', url)
  }, [title, description, pathname])
}

/**
 * Injects a JSON-LD block for the lifetime of the mounted component.
 *
 * Scoped to a route rather than sitting in `index.html` on purpose: a FAQPage
 * block must describe the page it is on, and a sitewide one would claim every
 * page is the FAQ.
 */
export function JsonLd({ data }) {
  useEffect(() => {
    const el = document.createElement('script')
    el.type = 'application/ld+json'
    el.textContent = JSON.stringify(data)
    document.head.appendChild(el)
    return () => {
      document.head.removeChild(el)
    }
  }, [data])
  return null
}

/**
 * Standard chrome for public marketing pages: sticky PublicNav, the page body,
 * and the shared Footer. Pages pass their content as children, a `title` for
 * the browser tab, and a `description` that becomes the meta description, the
 * Open Graph description, and the snippet a search engine is most likely to
 * show. Keep descriptions distinct per page; that is the entire point.
 */
export default function PublicLayout({ title, description, children }) {
  useSeo({ title, description })
  return (
    <div className="flex flex-col min-h-screen bg-white">
      <PublicNav />
      {/* Not <main> — App.jsx already provides the single main landmark this
          renders inside, and nesting two is a WCAG 1.3.1 failure. */}
      <div className="flex-1">{children}</div>
      <Footer />
    </div>
  )
}
